import { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandGroup, CommandItem, CommandEmpty } from '@/components/ui/command';
import { Check, Users } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * UserPickerPopover — Sélecteur utilisateur avec recherche as-you-type.
 *
 * Réutilisable partout où il faut choisir 1 utilisateur parmi une liste
 * (filtres tâches, calendrier, pipeline, factures, dashboard, teams…).
 *
 * Convention : les droits/visibilité sont TOUJOURS filtrés côté backend via
 * `usersAPI.getPicker()` ou équivalent — ce composant ne fait qu'afficher la
 * liste reçue en props avec une UX de recherche.
 *
 * Props :
 *   - users            : liste `[{id, first_name, last_name, email, role?}]`
 *   - value            : id sélectionné (string | null)
 *   - onChange(id|null): callback
 *   - placeholder      : texte quand rien de sélectionné
 *   - allowAll         : true → 1re option "Tous" (value=null / '')
 *   - allowMe          : true → 1re option "Moi-même" (nécessite `currentUserId`)
 *   - currentUserId    : id de l'utilisateur connecté (pour "Moi-même")
 *   - currentUserLabel : libellé du "Moi-même" (défaut "Moi-même")
 *   - showAllLabel     : libellé du "Tous" (défaut "Tous les utilisateurs")
 *   - align            : alignement du Popover ('start' | 'end' | 'center')
 *   - width            : largeur en px du PopoverContent (défaut 320)
 *   - testId           : data-testid du trigger
 *   - disabled         : bool
 *   - triggerClassName : classes CSS du bouton trigger
 */
export function UserPickerPopover({
    users = [],
    value,
    onChange,
    placeholder = 'Choisir un utilisateur…',
    allowAll = false,
    allowMe = false,
    currentUserId = null,
    currentUserLabel = 'Moi-même',
    showAllLabel = 'Tous les utilisateurs',
    align = 'start',
    width = 320,
    testId,
    disabled = false,
    triggerClassName,
}) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');

    const displayName = (u) => `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || '—';

    // Résolution du libellé du sélectionné.
    const selectedLabel = useMemo(() => {
        if (!value) return null;
        if (allowMe && currentUserId && value === currentUserId) return `${currentUserLabel}`;
        const u = users.find(x => x.id === value);
        return u ? displayName(u) : null;
    }, [value, users, allowMe, currentUserId, currentUserLabel]);

    // Filtrage — n'affiche les résultats qu'après ≥ 1 caractère quand la liste
    // est grande (>10). Pour listes courtes, on montre tout par défaut.
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const base = users;
        if (!q) return base.length <= 10 ? base : [];
        return base.filter(u =>
            (u.first_name?.toLowerCase().includes(q)) ||
            (u.last_name?.toLowerCase().includes(q)) ||
            (u.email?.toLowerCase().includes(q))
        ).slice(0, 50);
    }, [users, search]);

    const roleLabel = (u) => {
        if (u.role === 'admin_full' || u.role === 'admin') return 'Admin';
        if (u.role === 'vp') return 'VP';
        if (u.role === 'apporteur') return 'Apporteur';
        if (u.role === 'comptable') return 'Comptable';
        return u.role || '';
    };

    return (
        <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSearch(''); }}>
            <PopoverTrigger asChild disabled={disabled}>
                <button
                    type="button"
                    data-testid={testId}
                    disabled={disabled}
                    className={cn(
                        'flex h-9 w-full items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-left focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-60',
                        triggerClassName,
                    )}
                >
                    <span className="flex items-center gap-2 truncate">
                        <Users className="w-4 h-4 text-slate-400 shrink-0" />
                        {selectedLabel ? (
                            <span className="truncate">{selectedLabel}</span>
                        ) : value === null || value === '' ? (
                            allowAll ? <span className="truncate">{showAllLabel}</span> : <span className="text-slate-400 truncate">{placeholder}</span>
                        ) : (
                            <span className="text-slate-400 truncate">{placeholder}</span>
                        )}
                    </span>
                </button>
            </PopoverTrigger>
            <PopoverContent className="p-0" align={align} style={{ width }}>
                <Command shouldFilter={false}>
                    <CommandInput
                        autoFocus
                        placeholder="Tapez un nom…"
                        value={search}
                        onValueChange={setSearch}
                        data-testid={testId ? `${testId}-search` : undefined}
                    />
                    <CommandList>
                        {/* Options fixes en tête */}
                        {(allowAll || allowMe) && (
                            <CommandGroup>
                                {allowAll && (
                                    <CommandItem
                                        onSelect={() => { onChange?.(null); setOpen(false); setSearch(''); }}
                                        data-testid={testId ? `${testId}-all` : undefined}
                                    >
                                        <Check className={cn('mr-2 h-4 w-4', !value ? 'opacity-100' : 'opacity-0')} />
                                        {showAllLabel}
                                    </CommandItem>
                                )}
                                {allowMe && currentUserId && (
                                    <CommandItem
                                        onSelect={() => { onChange?.(currentUserId); setOpen(false); setSearch(''); }}
                                        data-testid={testId ? `${testId}-me` : undefined}
                                    >
                                        <Check className={cn('mr-2 h-4 w-4', value === currentUserId ? 'opacity-100' : 'opacity-0')} />
                                        {currentUserLabel}
                                    </CommandItem>
                                )}
                            </CommandGroup>
                        )}
                        {users.length > 10 && search.trim().length < 1 ? (
                            <div className="px-3 py-3 text-xs text-slate-500">
                                Tape au moins 1 caractère pour rechercher parmi {users.length} utilisateur(s).
                            </div>
                        ) : filtered.length === 0 ? (
                            <CommandEmpty>Aucun utilisateur trouvé.</CommandEmpty>
                        ) : (
                            <CommandGroup>
                                {filtered.map(u => (
                                    <CommandItem
                                        key={u.id}
                                        onSelect={() => { onChange?.(u.id); setOpen(false); setSearch(''); }}
                                        data-testid={testId ? `${testId}-user-${u.id}` : undefined}
                                    >
                                        <Check className={cn('mr-2 h-4 w-4', value === u.id ? 'opacity-100' : 'opacity-0')} />
                                        <span className="truncate">{displayName(u)}</span>
                                        {u.role && (
                                            <span className="ml-2 text-[11px] text-slate-400 shrink-0">({roleLabel(u)})</span>
                                        )}
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}

export default UserPickerPopover;
